import React from 'react';
import { motion } from 'framer-motion';
import { Wifi, AlertTriangle, Info, Download, Upload, Clock } from 'lucide-react';
import { formatPercentage, getCoverageClasses, getCoverageExplanation } from '../../utils/formatters';
import QuestionTooltip from '../shared/QuestionTooltip';
import { useWirelessAdvisor } from '../../context/WirelessAdvisorContext';

/**
 * CoverageAnalysis component
 * Shows a detailed analysis of the wireless coverage
 * 
 * @param {Object} props - Component props
 * @param {Object} props.coveragePercentages - Coverage percentages object { basic, medium, high }
 * @param {Object} props.coverage - Coverage object (might be deprecated or part of recalculation)
 * @param {Object} props.speedEstimates - Speed estimates object { basic, medium, high }
 * @param {string} props.internetSpeed - Internet speed category
 * @param {number} props.totalArea - Total area in square meters
 * @returns {JSX.Element} CoverageAnalysis component
 */
const CoverageAnalysis = ({ coveragePercentages, coverage, speedEstimates, internetSpeed, totalArea }) => {
  // Get custom coverage data from context (if recalculation logic uses it)
  // const { customCoverageData } = useWirelessAdvisor(); // Consider if this is still needed

  // Use props directly, providing defaults for safety
  const currentPercentages = coveragePercentages ?? { basic: 0, medium: 0, high: 0 };
  const currentSpeedEstimates = speedEstimates ?? {
    basic: { download: 'N/A', upload: 'N/A', latency: 'N/A' },
    medium: { download: 'N/A', upload: 'N/A', latency: 'N/A' },
    high: { download: 'N/A', upload: 'N/A', latency: 'N/A' }
  };
  // Assuming 'coverage' prop might hold area details if needed, otherwise calculate from totalArea and percentages
  const currentCoverage = coverage ?? {
      basicSpeed: (currentPercentages.basic / 100) * totalArea,
      mediumSpeed: (currentPercentages.medium / 100) * totalArea,
      highSpeed: (currentPercentages.high / 100) * totalArea,
  };

  // Extract speed estimates safely
  const highSpeedEst = currentSpeedEstimates.high ?? { download: 'N/A', upload: 'N/A', latency: 'N/A' };
  const mediumSpeedEst = currentSpeedEstimates.medium ?? { download: 'N/A', upload: 'N/A', latency: 'N/A' };
  const basicSpeedEst = currentSpeedEstimates.basic ?? { download: 'N/A', upload: 'N/A', latency: 'N/A' };


  // Get internet speed label
  const getInternetSpeedLabel = () => {
    switch (internetSpeed) {
      case 'slow':
        return 'Basic (< 50 Mbps)';
      case 'medium':
        return 'Standard (50-200 Mbps)';
      case 'fast':
        return 'Fast (200-500 Mbps)';
      case 'ultrafast':
        return 'Ultra-Fast (500+ Mbps)';
      default:
        return 'Standard';
    }
  };

  // Coverage explanations using corrected speed estimates
  const coverageExplanations = {
    high: `High-Speed Coverage (${highSpeedEst.download !== 'N/A' ? Math.round(highSpeedEst.download) + '+' : 'N/A'} Mbps)
• Best for 4K streaming, gaming, and video calls
• Typically within 10-15m of router with clear line of sight
• Significantly reduced by walls, floors, and distance
• Most reliable when device has direct view of router
• Recommended for home offices and gaming areas`,

    medium: `Medium-Speed Coverage (${mediumSpeedEst.download !== 'N/A' ? Math.round(mediumSpeedEst.download) + '+' : 'N/A'} Mbps)
• Suitable for HD streaming and regular internet use
• Extends 15-20m from router through 1-2 walls
• Coverage quality depends on:
  - Wall density and materials
  - Distance to router
  - Interference from other devices
• May experience speed variations during peak usage
• Best for general living areas and bedrooms`,

    basic: `Basic Coverage (${basicSpeedEst.download !== 'N/A' ? Math.round(basicSpeedEst.download) + '+' : 'N/A'} Mbps)
• Adequate for basic browsing and email
• Reaches 20-25m from router through multiple walls
• Most affected by:
  - Distance from router
  - Number of walls and floors
  - Building materials
• May have occasional connectivity issues
• Suitable for IoT devices and light internet usage`
  };
  
  // Get overall coverage assessment
  const getOverallAssessment = () => {
    // Use the safe currentPercentages
    if (currentPercentages.high >= 90) {
      return {
        rating: 'Excellent',
        description: 'Your home should have excellent high-speed coverage with this setup.',
        color: 'text-green-600'
      };
    } else if (currentPercentages.high >= 70) {
      return {
        rating: 'Very Good',
        description: 'Your home should have very good high-speed coverage with this setup.',
        color: 'text-green-500'
      };
    } else if (currentPercentages.high >= 50) {
      return {
        rating: 'Good',
        description: 'Your home should have good high-speed coverage in many areas.',
        color: 'text-blue-500'
      };
    } else if (currentPercentages.medium >= 80) {
      return {
        rating: 'Moderate',
        description: 'Your home should have moderate coverage, suitable for most standard activities.',
        color: 'text-yellow-500'
      };
    } else if (currentPercentages.medium >= 50) {
      return {
        rating: 'Fair',
        description: 'Your home should have fair coverage, but some areas might experience slower speeds.',
        color: 'text-orange-500'
      };
    } else {
      return {
        rating: 'Limited',
        description: 'Coverage appears limited with this setup; consider adding nodes or optimizing placement.',
        color: 'text-red-500'
      };
    }
  };
  
  const assessment = getOverallAssessment();
  
  return (
    <div className="space-y-6">
      {/* Overall Assessment */}
      <motion.div
        className="bg-white rounded-lg shadow-sm p-6 border border-gray-100"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h3 className="text-lg font-bold text-gray-900 mb-2">Overall Coverage Assessment</h3>
        <div className="flex items-center mb-2">
          <span className={`text-2xl font-bold ${assessment.color} mr-2`}>{assessment.rating}</span>
          <span className="text-gray-600">{assessment.description}</span>
        </div>
        <p className="text-gray-500 text-sm">
          {getCoverageExplanation(currentPercentages)}
        </p>
      </motion.div>
      
      {/* Coverage Percentages */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-gray-900">Coverage Breakdown</h3>
        
        {/* High-Speed Coverage */}
        <motion.div
          className="bg-blue-50 rounded-lg p-4 border border-blue-100"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center">
              <Wifi className="h-5 w-5 text-blue-600 mr-2" />
              <h4 className="text-md font-semibold text-blue-800">High-Speed Coverage</h4>
            </div>
            <div className="flex items-center">
              <span className="text-xl font-bold text-blue-700 mr-1">{formatPercentage(currentPercentages.high)}</span>
              <QuestionTooltip content={coverageExplanations.high} position="left" />
            </div>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2.5 mb-2">
            <div
              className="bg-blue-600 h-2.5 rounded-full"
              style={{ width: `${Math.min(100, currentPercentages.high || 0)}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-blue-700">
            <span>{Math.round(currentCoverage.highSpeed)}m² covered</span>
            <span>{Math.round(totalArea - currentCoverage.highSpeed)}m² not covered</span>
          </div>
        </motion.div>
        
        {/* Medium-Speed Coverage */}
        <motion.div
          className="bg-purple-50 rounded-lg p-4 border border-purple-100" // Changed color theme
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center">
              <Wifi className="h-5 w-5 text-purple-600 mr-2" />
              <h4 className="text-md font-semibold text-purple-800">Medium-Speed Coverage</h4>
            </div>
            <div className="flex items-center">
              <span className="text-xl font-bold text-purple-700 mr-1">{formatPercentage(currentPercentages.medium)}</span>
              <QuestionTooltip content={coverageExplanations.medium} position="left" />
            </div>
          </div>
          <div className="w-full bg-purple-200 rounded-full h-2.5 mb-2">
            <div
              className="bg-purple-500 h-2.5 rounded-full"
              style={{ width: `${Math.min(100, currentPercentages.medium || 0)}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-purple-700">
            <span>{Math.round(currentCoverage.mediumSpeed)}m² covered</span>
            <span>{Math.round(totalArea - currentCoverage.mediumSpeed)}m² not covered</span>
          </div>
        </motion.div>
        
        {/* Basic Coverage */}
        <motion.div
          className="bg-green-50 rounded-lg p-4 border border-green-100" // Changed color theme
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center">
              <Wifi className="h-5 w-5 text-green-600 mr-2" />
              <h4 className="text-md font-semibold text-green-800">Basic Coverage</h4>
            </div>
            <div className="flex items-center">
              <span className="text-xl font-bold text-green-700 mr-1">{formatPercentage(currentPercentages.basic)}</span>
              <QuestionTooltip content={coverageExplanations.basic} position="left" />
            </div>
          </div>
          <div className="w-full bg-green-200 rounded-full h-2.5 mb-2">
            <div
              className="bg-green-500 h-2.5 rounded-full"
              style={{ width: `${Math.min(100, currentPercentages.basic || 0)}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-green-700">
            <span>{Math.round(currentCoverage.basicSpeed)}m² covered</span>
            <span>{Math.round(totalArea - currentCoverage.basicSpeed)}m² not covered</span>
          </div>
        </motion.div>
      </div>
      
      {/* Speed Estimates */}
      <motion.div
        className="bg-indigo-50 rounded-lg p-6 border border-indigo-100"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <h3 className="text-lg font-bold text-indigo-900 mb-4">Speed Estimates vs Your Internet</h3>
        
        <div className="flex items-center mb-4">
          <div className="bg-indigo-100 p-2 rounded-lg mr-3">
            <Download className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <div className="text-sm text-indigo-600">Your Internet Connection</div>
            <div className="font-bold text-indigo-900">{getInternetSpeedLabel()}</div>
          </div>
        </div>
        
        <div className="space-y-4 mt-6">
          {/* High Speed */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-blue-100 p-2 rounded-lg mr-3">
                <Download className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-blue-600">High-Speed Areas</div>
                <div className="font-bold text-blue-900">{highSpeedEst.download !== 'N/A' ? Math.round(highSpeedEst.download) : 'N/A'} Mbps download</div>
                <div className="text-xs text-blue-700">{highSpeedEst.upload !== 'N/A' ? Math.round(highSpeedEst.upload) : 'N/A'} Mbps upload • {highSpeedEst.latency} latency</div>
              </div>
            </div>
            <div className="text-sm font-medium text-blue-800 bg-blue-100 px-3 py-1 rounded-full">
              {formatPercentage(currentPercentages.high)} of home
            </div>
          </div>
          
          {/* Medium Speed */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-purple-100 p-2 rounded-lg mr-3"> {/* Changed color */}
                <Download className="h-5 w-5 text-purple-600" /> {/* Changed color */}
              </div>
              <div>
                <div className="text-sm text-purple-600">Medium-Speed Areas</div> {/* Changed color */}
                <div className="font-bold text-purple-900">{mediumSpeedEst.download !== 'N/A' ? Math.round(mediumSpeedEst.download) : 'N/A'} Mbps download</div> {/* Changed color */}
                <div className="text-xs text-purple-700">{mediumSpeedEst.upload !== 'N/A' ? Math.round(mediumSpeedEst.upload) : 'N/A'} Mbps upload • {mediumSpeedEst.latency} latency</div> {/* Changed color */}
              </div>
            </div>
            <div className="text-sm font-medium text-purple-800 bg-purple-100 px-3 py-1 rounded-full"> {/* Changed color */}
              {formatPercentage(currentPercentages.medium)} of home
            </div>
          </div>
          
          {/* Basic Speed */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-green-100 p-2 rounded-lg mr-3"> {/* Changed color */}
                <Download className="h-5 w-5 text-green-600" /> {/* Changed color */}
              </div>
              <div>
                <div className="text-sm text-green-600">Basic-Speed Areas</div> {/* Changed color */}
                <div className="font-bold text-green-900">{basicSpeedEst.download !== 'N/A' ? Math.round(basicSpeedEst.download) : 'N/A'} Mbps download</div> {/* Changed color */}
                <div className="text-xs text-green-700">{basicSpeedEst.upload !== 'N/A' ? Math.round(basicSpeedEst.upload) : 'N/A'} Mbps upload • {basicSpeedEst.latency} latency</div> {/* Changed color */}
              </div>
            </div>
            <div className="text-sm font-medium text-green-800 bg-green-100 px-3 py-1 rounded-full"> {/* Changed color */}
              {formatPercentage(currentPercentages.basic)} of home
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Coverage Notes */}
      {currentPercentages.high < 70 && ( // Use safe currentPercentages
        <motion.div
          className="bg-amber-50 rounded-lg p-4 border border-amber-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-md font-semibold text-amber-800 mb-1">Coverage Improvement Opportunities</h4>
              <p className="text-sm text-amber-700">
                {currentPercentages.high < 50 // Use safe currentPercentages
                  ? "Your high-speed coverage is below 50%. Consider adding additional mesh units or optimizing placement."
                  : "Your high-speed coverage could be improved. Consider optimizing mesh unit placement."}
              </p>
              <ul className="mt-2 space-y-1 text-sm text-amber-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Position your primary router centrally and elevated for better signal propagation</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Keep mesh units away from metal objects, appliances, and thick walls</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Add more mesh units to improve coverage in specific areas</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Coverage Explanation */}
      <motion.div
        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <div className="flex items-start">
          <Info className="h-5 w-5 text-gray-500 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-md font-semibold text-gray-700 mb-1">About Coverage Analysis</h4>
            <p className="text-sm text-gray-600">
              This coverage analysis is based on theoretical models and typical home environments.
              Actual coverage and speeds may vary based on specific building materials, furniture placement,
              interference from other devices, and other factors unique to your home. The speed estimates
              show the maximum speeds you can expect in each coverage area, but actual speeds may be lower
              depending on your internet service provider and network conditions.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CoverageAnalysis;